import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from './ThemeContext';
import { useGuidedTour } from './GuidedTourContext';

export default function GuidedTour() {
  const { colors } = useTheme();
  const { currentStep, currentStepIndex, steps, isActive, nextStep, skipTour, completeTour } = useGuidedTour();
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});
  const [arrowDir, setArrowDir] = useState('left');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [waitingForEvent, setWaitingForEvent] = useState(false);
  const pollRef = useRef(null);
  const resizeRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Find target element and calculate position
  const findAndPosition = useCallback(() => {
    if (!currentStep) return;

    // Centered step (no target)
    if (!currentStep.target && !currentStep.mobileTarget) {
      setTargetRect(null);
      const w = window.innerWidth;
      const h = window.innerHeight;
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10001
      });
      setArrowDir('none');
      return;
    }

    const selector = isMobile && currentStep.mobileTarget
      ? currentStep.mobileTarget
      : currentStep.target;

    if (!selector) {
      setTargetRect(null);
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10001
      });
      setArrowDir('none');
      return;
    }

    const el = document.querySelector(selector);
    if (!el) {
      setTargetRect(null);
      return;
    }

    const rect = el.getBoundingClientRect();
    setTargetRect(rect);

    // Calculate tooltip position
    const pos = isMobile && currentStep.mobilePosition
      ? currentStep.mobilePosition
      : currentStep.position || 'right';

    const tooltipWidth = Math.min(320, window.innerWidth - 32);
    const tooltipHeight = 180; // estimate
    const pad = 16;

    let style = { position: 'fixed', zIndex: 10001, width: tooltipWidth + 'px' };
    let dir = 'left';

    if (pos === 'right') {
      style.top = rect.top + rect.height / 2 - tooltipHeight / 2;
      style.left = rect.right + pad;
      dir = 'left';
      // If overflows right edge, flip to left
      if (rect.right + pad + tooltipWidth > window.innerWidth) {
        style.left = rect.left - tooltipWidth - pad;
        dir = 'right';
      }
      // Clamp vertical
      if (style.top < pad) style.top = pad;
      if (style.top + tooltipHeight > window.innerHeight - pad) {
        style.top = window.innerHeight - pad - tooltipHeight;
      }
    } else if (pos === 'left') {
      style.top = rect.top + rect.height / 2 - tooltipHeight / 2;
      style.left = rect.left - tooltipWidth - pad;
      dir = 'right';
      if (style.left < pad) {
        style.left = rect.right + pad;
        dir = 'left';
      }
      if (style.top < pad) style.top = pad;
    } else if (pos === 'bottom') {
      style.top = rect.bottom + pad;
      style.left = rect.left + rect.width / 2 - tooltipWidth / 2;
      dir = 'top';
      // Clamp horizontal
      if (style.left < pad) style.left = pad;
      if (style.left + tooltipWidth > window.innerWidth - pad) {
        style.left = window.innerWidth - pad - tooltipWidth;
      }
      // If overflows bottom, flip to top
      if (rect.bottom + pad + tooltipHeight > window.innerHeight) {
        style.top = rect.top - tooltipHeight - pad;
        dir = 'bottom';
      }
    } else if (pos === 'top') {
      style.top = rect.top - tooltipHeight - pad;
      style.left = rect.left + rect.width / 2 - tooltipWidth / 2;
      dir = 'bottom';
      if (style.left < pad) style.left = pad;
      if (style.top < pad) {
        style.top = rect.bottom + pad;
        dir = 'top';
      }
    } else if (pos === 'center') {
      style.top = '50%';
      style.left = '50%';
      style.transform = 'translate(-50%, -50%)';
      dir = 'none';
    }

    // Convert numeric to px
    if (typeof style.top === 'number') style.top = style.top + 'px';
    if (typeof style.left === 'number') style.left = style.left + 'px';

    setTooltipStyle(style);
    setArrowDir(dir);
  }, [currentStep, isMobile]);

  // Poll for target element every 200ms
  useEffect(() => {
    if (!isActive || !currentStep) return;

    findAndPosition();
    pollRef.current = setInterval(findAndPosition, 200);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isActive, currentStep, findAndPosition]);

  // Recalculate on resize
  useEffect(() => {
    if (!isActive) return;
    const onResize = () => findAndPosition();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isActive, findAndPosition]);

  // When landing on a step with waitForEvent, hide the tour until the event fires
  useEffect(() => {
    if (!isActive || !currentStep?.waitForEvent) {
      setWaitingForEvent(false);
      return;
    }

    setWaitingForEvent(true);

    const handler = () => setWaitingForEvent(false);
    window.addEventListener(currentStep.waitForEvent, handler);
    return () => window.removeEventListener(currentStep.waitForEvent, handler);
  }, [isActive, currentStep]);

  // Handle click-target advance: intercept clicks on the target
  useEffect(() => {
    if (!isActive || !currentStep || currentStep.advance !== 'click-target') return;

    const selector = isMobile && currentStep.mobileTarget
      ? currentStep.mobileTarget
      : currentStep.target;
    if (!selector) return;

    const handler = () => {
      // Small delay to let the actual click handler fire first
      setTimeout(() => nextStep(), 100);
    };

    // Poll until we find the element and attach the listener
    let el = null;
    const interval = setInterval(() => {
      el = document.querySelector(selector);
      if (el) {
        el.addEventListener('click', handler, { once: true });
        clearInterval(interval);
      }
    }, 200);

    return () => {
      clearInterval(interval);
      if (el) el.removeEventListener('click', handler);
    };
  }, [isActive, currentStep, currentStepIndex, isMobile, nextStep]);

  // Boost mobile sidebar z-index when targeting mobile menu items
  useEffect(() => {
    if (!isActive || !currentStep || !isMobile) return;

    const isMobileMenuItem = currentStep.mobileTarget &&
      currentStep.mobileTarget.includes('menu-mobile-');

    if (isMobileMenuItem) {
      // Find the mobile sidebar overlay and boost its z-index
      const sidebarOverlay = document.querySelector('[data-tour-id="mobile-sidebar-overlay"]');
      if (sidebarOverlay) {
        const originalZ = sidebarOverlay.style.zIndex;
        sidebarOverlay.style.zIndex = '10000';
        return () => {
          sidebarOverlay.style.zIndex = originalZ || '';
        };
      }
    }
  }, [isActive, currentStep, isMobile]);

  if (!isActive || !currentStep || waitingForEvent) return null;

  const isClickTarget = currentStep.advance === 'click-target';
  const isLastStep = currentStepIndex === steps.length - 1;
  const isCentered = !currentStep.target && !currentStep.mobileTarget;

  // Spotlight overlay with cutout
  const renderOverlay = () => {
    if (!targetRect || isCentered) {
      // Full dark overlay, no cutout
      return (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 10000,
            pointerEvents: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    const pad = 8;
    return (
      <>
        {/* Spotlight element with box-shadow cutout */}
        <div
          style={{
            position: 'fixed',
            top: targetRect.top - pad,
            left: targetRect.left - pad,
            width: targetRect.width + pad * 2,
            height: targetRect.height + pad * 2,
            borderRadius: '8px',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.7)',
            zIndex: 10000,
            pointerEvents: 'none'
          }}
        />
        {/* Click-blocking layer over the rest of the screen */}
        {/* Top */}
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          height: Math.max(0, targetRect.top - pad),
          zIndex: 10000, pointerEvents: 'auto'
        }} onClick={(e) => e.stopPropagation()} />
        {/* Bottom */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          height: Math.max(0, window.innerHeight - targetRect.bottom - pad),
          zIndex: 10000, pointerEvents: 'auto'
        }} onClick={(e) => e.stopPropagation()} />
        {/* Left */}
        <div style={{
          position: 'fixed',
          top: targetRect.top - pad,
          left: 0,
          width: Math.max(0, targetRect.left - pad),
          height: targetRect.height + pad * 2,
          zIndex: 10000, pointerEvents: 'auto'
        }} onClick={(e) => e.stopPropagation()} />
        {/* Right */}
        <div style={{
          position: 'fixed',
          top: targetRect.top - pad,
          left: targetRect.right + pad,
          right: 0,
          height: targetRect.height + pad * 2,
          zIndex: 10000, pointerEvents: 'auto'
        }} onClick={(e) => e.stopPropagation()} />
      </>
    );
  };

  // Pulse ring for click-target steps
  const renderPulse = () => {
    if (!isClickTarget || !targetRect || isCentered) return null;
    return (
      <div style={{
        position: 'fixed',
        top: targetRect.top + targetRect.height / 2,
        left: targetRect.left + targetRect.width / 2,
        width: 0,
        height: 0,
        zIndex: 10000,
        pointerEvents: 'none'
      }}>
        <div style={{
          position: 'absolute',
          top: '-20px',
          left: '-20px',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          border: `3px solid ${colors.accent}`,
          animation: 'tourPulse 1.5s ease-out infinite'
        }} />
      </div>
    );
  };

  // Arrow for tooltip
  const renderArrow = () => {
    if (arrowDir === 'none') return null;
    const size = 8;
    const arrowBase = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid'
    };

    if (arrowDir === 'left') {
      return (
        <div style={{
          ...arrowBase,
          left: -size,
          top: '50%',
          marginTop: -size,
          borderWidth: `${size}px ${size}px ${size}px 0`,
          borderColor: `transparent ${colors.bgCard} transparent transparent`
        }} />
      );
    }
    if (arrowDir === 'right') {
      return (
        <div style={{
          ...arrowBase,
          right: -size,
          top: '50%',
          marginTop: -size,
          borderWidth: `${size}px 0 ${size}px ${size}px`,
          borderColor: `transparent transparent transparent ${colors.bgCard}`
        }} />
      );
    }
    if (arrowDir === 'top') {
      return (
        <div style={{
          ...arrowBase,
          top: -size,
          left: '50%',
          marginLeft: -size,
          borderWidth: `0 ${size}px ${size}px ${size}px`,
          borderColor: `transparent transparent ${colors.bgCard} transparent`
        }} />
      );
    }
    if (arrowDir === 'bottom') {
      return (
        <div style={{
          ...arrowBase,
          bottom: -size,
          left: '50%',
          marginLeft: -size,
          borderWidth: `${size}px ${size}px 0 ${size}px`,
          borderColor: `${colors.bgCard} transparent transparent transparent`
        }} />
      );
    }
    return null;
  };

  // Don't render tooltip if we don't have position yet (still polling for element)
  const hasPosition = tooltipStyle.top || tooltipStyle.transform;

  return (
    <>
      {/* CSS animations */}
      <style>{`
        @keyframes tourPulse {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(3); opacity: 0; }
        }
        @keyframes tourFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {renderOverlay()}
      {renderPulse()}

      {/* Tooltip card */}
      {hasPosition && (
        <div style={{
          ...tooltipStyle,
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.5rem',
          border: `1px solid ${colors.border}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          maxWidth: '320px',
          animation: 'tourFadeIn 0.3s ease-out'
        }}>
          {renderArrow()}

          {/* Step indicator */}
          <div style={{
            display: 'flex',
            gap: '4px',
            marginBottom: '0.75rem'
          }}>
            {steps.map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === currentStepIndex ? '20px' : '8px',
                  height: '4px',
                  borderRadius: '2px',
                  background: i === currentStepIndex ? colors.accent : colors.border,
                  transition: 'all 0.3s'
                }}
              />
            ))}
          </div>

          {/* Title */}
          <h3 style={{
            margin: '0 0 0.5rem',
            fontSize: '1.1rem',
            fontWeight: '700',
            color: colors.text
          }}>
            {currentStep.title}
          </h3>

          {/* Content */}
          <p style={{
            margin: '0 0 1.25rem',
            fontSize: '0.9rem',
            color: colors.textMuted,
            lineHeight: 1.5
          }}>
            {isMobile && currentStep.mobileContent ? currentStep.mobileContent : currentStep.content}
          </p>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <button
              onClick={skipTour}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textMuted,
                fontSize: '0.85rem',
                cursor: 'pointer',
                padding: '0.5rem 0'
              }}
            >
              Skip tour
            </button>

            {currentStep.advance === 'next-button' && (
              <button
                onClick={isLastStep ? completeTour : nextStep}
                style={{
                  padding: '0.5rem 1.25rem',
                  background: colors.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {isLastStep ? 'Done' : 'Next'}
              </button>
            )}

            {currentStep.advance === 'click-target' && (
              <span style={{
                fontSize: '0.8rem',
                color: colors.accent,
                fontStyle: 'italic'
              }}>
                Click the highlighted area
              </span>
            )}
          </div>
        </div>
      )}
    </>
  );
}
